import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Row, Col, Switch, message,
} from 'antd';
import dayjs from 'dayjs';
import { createCalendarEvent, updateCalendarEvent } from '../services/calendarService';
import { searchEntities } from '../services/callService';

const { TextArea } = Input;

// Create / edit a custom calendar event (optionally linked to a lead/client).
const EventFormModal = ({ open, onClose, onSaved, editing, settings = {} }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [leadOpts, setLeadOpts] = useState([]);

  const eventTypes = settings.event_type || [];
  const executives = settings.executive || [];

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        ...editing,
        StartAt: editing.StartAt ? dayjs(editing.StartAt) : null,
        EndAt: editing.EndAt ? dayjs(editing.EndAt) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ EventType: 'Team Meeting', StartAt: dayjs().add(1, 'hour').startOf('hour') });
    }
  }, [open, editing, form]);

  const searchLead = async (q) => {
    if (!q || q.length < 2) { setLeadOpts([]); return; }
    try { setLeadOpts(await searchEntities('lead', q)); } catch { /* ignore */ }
  };

  const handleOk = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    const payload = {
      ...v,
      StartAt: v.StartAt ? v.StartAt.format('YYYY-MM-DD HH:mm:ss') : null,
      EndAt: v.EndAt ? v.EndAt.format('YYYY-MM-DD HH:mm:ss') : null,
    };
    setSaving(true);
    try {
      if (editing) { await updateCalendarEvent(editing.EventID, payload); message.success('Event updated'); }
      else { await createCalendarEvent(payload); message.success('Event created'); }
      onSaved && onSaved();
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to save event');
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title={editing ? 'Edit Event' : 'New Event'}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={saving}
      okText={editing ? 'Save' : 'Create'}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="Title" label="Title" rules={[{ required: true, message: 'Enter a title' }]}>
          <Input size="large" placeholder="e.g. Vendor meeting with XYZ" />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="EventType" label="Type">
              <Select size="large" showSearch
                options={eventTypes.map(t => ({ value: t.Value, label: t.Label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="ExecutiveName" label="Executive">
              <Select size="large" allowClear showSearch
                options={executives.map(e => ({ value: e.Value, label: e.Label }))} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="StartAt" label="Start" rules={[{ required: true, message: 'Pick a start time' }]}>
              <DatePicker showTime style={{ width: '100%' }} size="large" format="DD MMM YYYY, hh:mm A" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="EndAt" label="End (optional)">
              <DatePicker showTime style={{ width: '100%' }} size="large" format="DD MMM YYYY, hh:mm A" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="CustomerName" label="Customer / With">
              <Input size="large" placeholder="Optional" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="CustomerMobile" label="Mobile">
              <Input size="large" placeholder="Optional" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="LeadID" label="Link to Lead (optional)">
          <Select showSearch filterOption={false} allowClear size="large" placeholder="Search a lead to link…"
            onSearch={searchLead}
            options={leadOpts.map(o => ({ value: o.id, label: `${o.Name} — ${o.Mobile || ''}` }))} />
        </Form.Item>
        <Form.Item name="Notes" label="Notes">
          <TextArea rows={3} placeholder="Details…" />
        </Form.Item>
        <Form.Item name="AllDay" label="All day" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EventFormModal;
